// The Ask Optivo live assistant: the wire to `optivo-service/src/routes/assistant.ts`.
//
// `GET /api/assistant` says whether the assistant is offered, the day's limits
// and the caller's open conversation on the open property; a question goes as
// `POST /api/assistant/messages` and comes back as server-sent events read off
// the response body (`serverSentEvents.ts`). Every write names the property
// its page was loaded for (`?connectionId=`): the server refuses one that
// names none, and one that names another property than the open one.
//
// Everything read is read defensively: the page and the service deploy
// separately and a tab stays open for days, so a value this page has no word
// for is read as nothing rather than trusted. No message text is ever logged
// or thrown from here.

import { apiFetch } from "./apiClient";
import { activeConnectionChangedError, isActiveConnectionChangedRefusal, withPageConnection } from "./loadedConnection";
import { readServerSentEvents } from "./serverSentEvents";

/** A stream that has said nothing — not even a heartbeat — for this long is given up as interrupted. */
export const ASSISTANT_STREAM_IDLE_MS = 45_000;

/** The longest question the server takes, when it does not say. */
export const ASSISTANT_DEFAULT_MAX_MESSAGE_CHARS = 2000;

export type AssistantLocale = "cs" | "en";
export type AssistantStopReason = "end_turn" | "max_tokens" | "refusal" | "iteration_limit" | "timeout";
export type AssistantMessageStatus = "complete" | "interrupted" | "failed" | "streaming";
export type AssistantFeedback = "up" | "down" | null;
/**
 * Why the assistant is not offered to the caller on the open property: one of
 * the server's own reasons, or `not_enabled` — the server could, and no
 * administrator has switched it on for the caller (it is rolled out person by
 * person).
 */
export type AssistantUnavailableReason = "not_configured" | "switched_off" | "not_enabled";

export interface AssistantUserMessage {
    id: string;
    role: "user";
    text: string;
    createdAt: string;
}

export interface AssistantAnswerMessage {
    id: string;
    role: "assistant";
    text: string;
    createdAt: string;
    status: AssistantMessageStatus;
    stopReason: AssistantStopReason | null;
    feedback: AssistantFeedback;
    /** Every assistant message is written by the AI (EU AI Act Art. 50). */
    generatedBy: "ai";
}

export type AssistantMessage = AssistantUserMessage | AssistantAnswerMessage;

export interface AssistantConversation {
    id: string;
    status: "open" | "handed_over";
    createdAt: string;
    handedOver: { requestId: number; caseNumber: string } | null;
    messages: AssistantMessage[];
}

export interface AssistantLimits {
    /** The caller's questions a day; null when the server did not say. */
    messagesPerDay: number | null;
    usedToday: number;
    maxMessageChars: number;
}

export interface AssistantState {
    available: boolean;
    unavailableReason: AssistantUnavailableReason | null;
    limits: AssistantLimits;
    retentionDays: number | null;
    /** The strategy pricing the property, which `/strategies/active/…` links open. */
    activeStrategyId: string | null;
    conversation: AssistantConversation | null;
}

/** What the operator was looking at when they asked (`optivo-service/src/services/assistant/pageContext.ts`). */
export interface AssistantPageContext {
    path: string;
    page: string | null;
    tab?: string | null;
    section?: string | null;
    step?: string | null;
    unsavedEdits?: boolean;
    expertMode?: boolean;
}

export type AssistantStreamEvent =
    | { type: "start"; conversationId: string; userMessageId: string; assistantMessageId: string; activeStrategyId: string | null }
    | { type: "tool"; name: string; phase: "start" | "end"; ok: boolean | null; subject: string | null }
    | { type: "text"; delta: string }
    | { type: "done"; messageId: string; stopReason: AssistantStopReason | null }
    | { type: "error"; code: string };

/** How a stream ended: with `done`, with `error`, or neither (cut off, or silent for too long). */
export type AssistantStreamOutcome = "done" | "error" | "interrupted";

/** A refusal before a stream opened, or of one of the conversation's other writes, with the server's code. */
export class AssistantRequestError extends Error {
    readonly code: string | null;
    readonly status: number;
    /** The day's limit of questions, on the refusal that met it. */
    readonly limit: number | null;

    constructor(message: string, code: string | null, status: number, limit: number | null = null) {
        super(message);
        this.name = "AssistantRequestError";
        this.code = code;
        this.status = status;
        this.limit = limit;
    }
}

// ---------------------------------------------------------------------------
// Reading what the server sent
// ---------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOr<T>(value: unknown, fallback: T): string | T {
    return typeof value === "string" ? value : fallback;
}

function wholeNumberOr<T>(value: unknown, fallback: T): number | T {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback;
}

const STOP_REASONS: readonly AssistantStopReason[] = ["end_turn", "max_tokens", "refusal", "iteration_limit", "timeout"];
const MESSAGE_STATUSES: readonly AssistantMessageStatus[] = ["complete", "interrupted", "failed", "streaming"];
const UNAVAILABLE_REASONS: readonly AssistantUnavailableReason[] = [
    "not_configured", "switched_off", "not_enabled",
];

function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
    return typeof value === "string" && (list as readonly string[]).includes(value) ? (value as T) : null;
}

function readMessage(raw: unknown): AssistantMessage | null {
    if (!isRecord(raw) || typeof raw.id !== "string") return null;
    const createdAt = stringOr(raw.createdAt, "");
    if (raw.role === "user") {
        return { id: raw.id, role: "user", text: stringOr(raw.text, ""), createdAt };
    }
    if (raw.role !== "assistant") return null;
    return {
        id: raw.id,
        role: "assistant",
        text: stringOr(raw.text, ""),
        createdAt,
        status: oneOf(MESSAGE_STATUSES, raw.status) ?? "complete",
        stopReason: oneOf(STOP_REASONS, raw.stopReason),
        feedback: raw.feedback === "up" || raw.feedback === "down" ? raw.feedback : null,
        generatedBy: "ai",
    };
}

function readConversation(raw: unknown): AssistantConversation | null {
    if (!isRecord(raw) || typeof raw.id !== "string") return null;
    const handedOver = isRecord(raw.handedOver) && typeof raw.handedOver.requestId === "number" && typeof raw.handedOver.caseNumber === "string"
        ? { requestId: raw.handedOver.requestId, caseNumber: raw.handedOver.caseNumber }
        : null;
    return {
        id: raw.id,
        status: raw.status === "handed_over" || handedOver ? "handed_over" : "open",
        createdAt: stringOr(raw.createdAt, ""),
        handedOver,
        messages: (Array.isArray(raw.messages) ? raw.messages : [])
            .map(readMessage)
            .filter((message): message is AssistantMessage => message !== null),
    };
}

function readLimits(raw: unknown): AssistantLimits {
    const limits = isRecord(raw) ? raw : {};
    return {
        messagesPerDay: wholeNumberOr(limits.messagesPerDay, null),
        usedToday: wholeNumberOr(limits.usedToday, 0),
        maxMessageChars: wholeNumberOr(limits.maxMessageChars, null) || ASSISTANT_DEFAULT_MAX_MESSAGE_CHARS,
    };
}

/** The refusal a non-stream answer carries: the shell's error for another property, else the server's code. */
async function refusalOf(res: Response, fallback: string): Promise<Error> {
    const data: unknown = await res.json().catch(() => ({}));
    const body = isRecord(data) ? data : {};
    const message = typeof body.error === "string" && body.error ? body.error : `${fallback}: ${res.status}`;
    if (isActiveConnectionChangedRefusal(body)) return activeConnectionChangedError(message);
    return new AssistantRequestError(
        message,
        typeof body.code === "string" ? body.code : null,
        res.status,
        wholeNumberOr(body.limit, null),
    );
}

/**
 * `path` naming the property the page was loaded for — the shell's — or the
 * window's own while the shell has not said: the server refuses an assistant
 * write that names none.
 */
function namingProperty(path: string, connectionId: number): string {
    const named = withPageConnection(path);
    if (named !== path) return named;
    const separator = path.includes("?") ? "&" : "?";
    return `${path}${separator}connectionId=${encodeURIComponent(String(connectionId))}`;
}

// ---------------------------------------------------------------------------
// The calls
// ---------------------------------------------------------------------------

export async function fetchAssistantState(): Promise<AssistantState> {
    const res = await apiFetch("/api/assistant");
    if (!res.ok) throw new Error(`fetchAssistantState failed: ${res.status}`);
    const data: unknown = await res.json();
    if (!isRecord(data) || data.success !== true) throw new Error("Failed to read the assistant");
    return {
        available: data.available === true,
        unavailableReason: oneOf(UNAVAILABLE_REASONS, data.unavailableReason),
        limits: readLimits(data.limits),
        retentionDays: wholeNumberOr(data.retentionDays, null),
        activeStrategyId: typeof data.activeStrategyId === "string" && data.activeStrategyId ? data.activeStrategyId : null,
        conversation: readConversation(data.conversation),
    };
}

export interface AssistantQuestion {
    /** The property the window is for, named where the shell has not named the page's. */
    connectionId: number;
    /** Null starts a new conversation. */
    conversationId: string | null;
    message: string;
    locale: AssistantLocale;
    page: AssistantPageContext | null;
}

function readStreamEvent(name: string, raw: string): AssistantStreamEvent | null {
    let data: unknown;
    try {
        data = JSON.parse(raw);
    } catch {
        return null;
    }
    if (!isRecord(data)) return null;
    switch (name) {
        case "start":
            if (typeof data.conversationId !== "string" || typeof data.assistantMessageId !== "string") return null;
            return {
                type: "start",
                conversationId: data.conversationId,
                userMessageId: stringOr(data.userMessageId, ""),
                assistantMessageId: data.assistantMessageId,
                activeStrategyId: typeof data.activeStrategyId === "string" && data.activeStrategyId ? data.activeStrategyId : null,
            };
        case "tool":
            if (typeof data.name !== "string" || (data.phase !== "start" && data.phase !== "end")) return null;
            return {
                type: "tool",
                name: data.name,
                phase: data.phase,
                ok: typeof data.ok === "boolean" ? data.ok : null,
                subject: typeof data.subject === "string" ? data.subject : null,
            };
        case "text":
            return typeof data.delta === "string" ? { type: "text", delta: data.delta } : null;
        case "done":
            return { type: "done", messageId: stringOr(data.messageId, ""), stopReason: oneOf(STOP_REASONS, data.stopReason) };
        case "error":
            return { type: "error", code: stringOr(data.code, "ASSISTANT_FAILED") };
        default:
            // An event this page has no word for.
            return null;
    }
}

function abortError(): DOMException {
    return new DOMException("The answer was stopped", "AbortError");
}

/**
 * Asks the question and hands every event of the answer to `onEvent` as it
 * arrives. Resolves with how the stream ended; rejects with an `AbortError`
 * when `signal` stops it, and with {@link AssistantRequestError} (or the
 * shell's error for another property) when the server refuses it before a
 * stream opens.
 *
 * The caller's signal is linked to the request's by hand (`AbortSignal.any`
 * needs Safari 17.4), and a stream that has sent nothing at all — not even a
 * heartbeat — for {@link ASSISTANT_STREAM_IDLE_MS} is given up as interrupted:
 * the server sends one every 15 seconds, so silence that long is a connection
 * that is gone.
 */
export async function streamAssistantAnswer(
    question: AssistantQuestion,
    onEvent: (event: AssistantStreamEvent) => void,
    signal: AbortSignal,
): Promise<AssistantStreamOutcome> {
    if (signal.aborted) throw abortError();
    const request = new AbortController();
    const follow = () => request.abort();
    signal.addEventListener("abort", follow, { once: true });
    let idle: ReturnType<typeof setTimeout> | null = null;
    let wentQuiet = false;
    const watch = () => {
        if (idle !== null) clearTimeout(idle);
        idle = setTimeout(() => {
            wentQuiet = true;
            request.abort();
        }, ASSISTANT_STREAM_IDLE_MS);
    };
    let outcome: AssistantStreamOutcome = "interrupted";
    try {
        watch();
        let res: Response;
        try {
            res = await apiFetch(namingProperty("/api/assistant/messages", question.connectionId), {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
                body: JSON.stringify({
                    conversationId: question.conversationId,
                    message: question.message,
                    locale: question.locale,
                    page: question.page,
                }),
                signal: request.signal,
            });
        } catch (err) {
            if (signal.aborted) throw abortError();
            if (wentQuiet) return "interrupted";
            throw err;
        }
        if (signal.aborted) throw abortError();
        const isStream = (res.headers.get("content-type") ?? "").startsWith("text/event-stream");
        if (!res.ok || !isStream || !res.body) throw await refusalOf(res, "streamAssistantAnswer failed");
        try {
            for await (const sse of readServerSentEvents(res.body, watch, request.signal)) {
                const event = readStreamEvent(sse.event, sse.data);
                if (!event) continue;
                if (event.type === "done") outcome = "done";
                if (event.type === "error") outcome = "error";
                onEvent(event);
                if (outcome !== "interrupted") break;
            }
        } catch (err) {
            // Cut off mid-stream: what arrived stands, and the conversation is read again.
            if (signal.aborted) throw abortError();
            if (outcome !== "interrupted") return outcome;
            if (!wentQuiet && !(err instanceof TypeError) && !(err instanceof DOMException)) throw err;
            return "interrupted";
        }
        if (signal.aborted && outcome === "interrupted") throw abortError();
        return outcome;
    } finally {
        if (idle !== null) clearTimeout(idle);
        signal.removeEventListener("abort", follow);
    }
}

export async function closeAssistantConversation(conversationId: string, connectionId: number): Promise<void> {
    const res = await apiFetch(
        namingProperty(`/api/assistant/conversations/${encodeURIComponent(conversationId)}/close`, connectionId),
        { method: "POST" },
    );
    if (!res.ok) throw await refusalOf(res, "closeAssistantConversation failed");
}

export interface AssistantHandover {
    /** The ordinary question the conversation became, as the rail shows one. */
    request: { id: number; kind: "question"; createdAt: string };
    caseNumber: string;
    supportNotified: boolean;
}

export async function handOverAssistantConversation(
    conversationId: string,
    note: string | null,
    connectionId: number,
): Promise<AssistantHandover> {
    const res = await apiFetch(
        namingProperty(`/api/assistant/conversations/${encodeURIComponent(conversationId)}/handover`, connectionId),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(note ? { note } : {}),
        },
    );
    if (!res.ok) throw await refusalOf(res, "handOverAssistantConversation failed");
    const data: unknown = await res.json();
    if (!isRecord(data) || data.success !== true || !isRecord(data.request) || typeof data.request.id !== "number") {
        throw new Error("Failed to hand the conversation over");
    }
    return {
        request: { id: data.request.id, kind: "question", createdAt: stringOr(data.request.createdAt, new Date().toISOString()) },
        caseNumber: stringOr(data.caseNumber, `OPT-${data.request.id}`),
        supportNotified: data.supportNotified === true,
    };
}

export async function sendAssistantFeedback(messageId: string, value: AssistantFeedback, connectionId: number): Promise<void> {
    const res = await apiFetch(
        namingProperty(`/api/assistant/messages/${encodeURIComponent(messageId)}/feedback`, connectionId),
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value }),
        },
    );
    if (!res.ok) throw await refusalOf(res, "sendAssistantFeedback failed");
}
